import React, { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../../helpr/queryClient';
import ListingCalendarService from '../../services/listing-calendar-service';
import { formatLocalISO, startOfMonthFrom } from './utils/date-utils';
import { useCalendarData } from './hooks/useCalendarData';
import { useCalendarDates } from './hooks/useCalendarDates';
import { CalendarHeader } from './components/CalendarHeader';
import { CalendarGrid } from './components/CalendarGrid';
import './listing-calendar.scss';

export const Calendar = ({
  onDateSelect,
  extraDates = [],
  minSelectableDate = null,
  maxSelectableDate = null,
  initialVisibleDate = null,
}) => {

  const listingCalendarService = React.useMemo(() => new ListingCalendarService(), []);
  const parsedInitialMonthStart = React.useMemo(() => startOfMonthFrom(initialVisibleDate), [initialVisibleDate]);

  const [viewDate, setViewDate] = useState(() => {
    return parsedInitialMonthStart ? new Date(parsedInitialMonthStart) : new Date();
  });
  const [error, setError] = useState(null);

  const { data: apiPayload = {}, isLoading, isError, error: queryError } = useCalendarData(listingCalendarService);

  const apiBlockDates = apiPayload.blockDates || [];
  const apiHolidays = apiPayload.holidays || [];

  const { computedMinDate, computedMaxDate, reservedSet, holidaySet } = useCalendarDates(
    maxSelectableDate,
    apiPayload,
    extraDates,
    apiBlockDates,
    apiHolidays
  );

  useEffect(() => {
    if (parsedInitialMonthStart) {
      if (
        viewDate.getFullYear() !== parsedInitialMonthStart.getFullYear() ||
        viewDate.getMonth() !== parsedInitialMonthStart.getMonth()
      ) {
        setViewDate(new Date(parsedInitialMonthStart));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedInitialMonthStart]);

  useEffect(() => {
    setError(null);
  }, []);

  useEffect(() => {
    if (isError) {
      setError('Unable to load calendar data.');
      console.error('Calendar query error', queryError);
    }
  }, [isError, queryError]);

  // clamp viewDate if it's beyond max
  useEffect(() => {
    const v = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const maxMonthStart = new Date(computedMaxDate.getFullYear(), computedMaxDate.getMonth(), 1);
    if (v > maxMonthStart) {
      setViewDate(new Date(maxMonthStart));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedMaxDate]);

  if (isLoading) {
    return (
      <div className="calendar bg-white border p-3 shadow-sm rounded">
        <Spinner animation="border" role="status" aria-hidden="true" />
        <span className="ms-2">Loading calendar…</span>
      </div>
    );
  }

  if (error) {
    return <div className="text-danger py-3">{error}</div>;
  }

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayIso = formatLocalISO(todayStart);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const maxMonthStart = new Date(computedMaxDate.getFullYear(), computedMaxDate.getMonth(), 1);
  const minMonthStart = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);

  const handlePrevMonth = () => {
    const prev = new Date(year, month - 1, 1);
    if (prev < minMonthStart) return;
    setViewDate(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(year, month + 1, 1);
    if (next > maxMonthStart) return;
    setViewDate(next);
  };

  const prevDisabled = new Date(year, month - 1, 1) < minMonthStart;
  const nextDisabled = new Date(year, month + 1, 1) > maxMonthStart;

  return (
    <div className="calendar bg-white border p-3 shadow-sm rounded">
      <CalendarHeader
        viewDate={viewDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        prevDisabled={prevDisabled}
        nextDisabled={nextDisabled}
      />

      <div className="weekdays small text-center text-muted mb-1">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d}>{d}</div>)}
      </div>

      <CalendarGrid
        viewDate={viewDate}
        computedMinDate={computedMinDate}
        computedMaxDate={computedMaxDate}
        reservedSet={reservedSet}
        holidaySet={holidaySet}
        todayIso={todayIso}
        onDateSelect={onDateSelect}
      />
    </div>
  );
};

export const CalendarWithQuery = (props) => (
  <QueryClientProvider client={queryClient}>
    <Calendar {...props} />
  </QueryClientProvider>
);

export default CalendarWithQuery;
